/**
 * Financial Reports Page (English)
 * /en/financial-reports/
 */
import React, { useState } from 'react'
import { graphql, PageProps } from 'gatsby'
import styled from '@emotion/styled'
import get from 'lodash.get'
import Layout from '../../components/Layout'
import HeroBanner from '../../components/HeroBanner'
import SEO from '../../components/SEO'
import PageTitle from '../../components/PageTitle'
import EmptyState from '../../components/EmptyState'
import Pagination from '../../components/Pagination'
import { FinancialReportConnection } from '../../types'
import financialReportsData from '../../data/en-financial-reports.json'

// ─── Styled Components ────────────────────────────────────────────────────────

const ReportsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  min-height: 60vh;
  width: 834px;
  max-width: 95%;
  border-spacing: 0;
  overflow: hidden;
  padding: 0 0 10px;
  margin: 0 auto;
  height: auto;

  @media (max-width: 767px) {
    width: 100%;
    max-width: 100%;
    padding: 0 1.25rem 10px;
  }
`

const ReportCard = styled.a`
  width: calc(100% / 3);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  height: 200px;
  margin-bottom: 6rem;
  text-decoration: none;
  color: inherit;

  @media (max-width: 767px) {
    width: calc(100% / 2);
    height: 180px;
    margin-bottom: 5rem;
  }

  @media (max-width: 340px) {
    width: 100%;
    height: 220px;
    margin-bottom: 5rem;
  }
`

const ReportThumbnailImg = styled.img`
  margin: 1.2rem 0;
  max-height: 100%;
  width: auto;
  box-shadow: 0 5px 30px -9px rgba(0, 0, 0, 0.5);

  @media (max-width: 767px) {
    max-height: 100%;
    max-width: 90%;
  }
`

const ReportName = styled.span`
  font-size: 16px;
  color: #666;
  text-align: center;
  padding: 0 1rem;
  text-overflow: ellipsis;
  -webkit-line-clamp: 3;

  @media (max-width: 767px) {
    font-size: 14px;
    padding: 0 0.5rem;
  }
`

// ─── GraphQL Query ────────────────────────────────────────────────────────────

export const query = graphql`
  query FinancialReportsEnQuery {
    records: allSanityRecord(filter: { category: { name: { en: { eq: "Financial Reports" } } } }, sort: { fields: date, order: DESC }) {
      totalCount
      edges {
        node {
          id
          name {
            zhHant
            en
          }
          file {
            en {
              asset {
                url
              }
            }
            zhHant {
              asset {
                url
              }
            }
          }
          thumbnail {
            asset {
              _id
              url
            }
          }
        }
      }
    }
  }
`

// ─── Component ────────────────────────────────────────────────────────────────

interface FinancialReportsEnPageProps extends PageProps {
  data?: {
    records: FinancialReportConnection
  }
}

const PER_PAGE = 30
const PAGE_TITLE = 'Financial Reports'
const LANG_KEY = 'en' as const
const LANG_FIELD = 'en' as const

const getReportSortMeta = (name: string) => {
  const yearMatch = name.match(/20\d{2}/)
  const year = yearMatch ? Number(yearMatch[0]) : 0
  const normalized = name.toLowerCase()
  const typeRank = /annual|年報/.test(normalized)
    ? 1
    : /interim|中期/.test(normalized)
    ? 2
    : /esg|environment|環境|社會|管治/.test(normalized)
    ? 3
    : 9

  return { year, typeRank }
}

const sortFinancialReportEdges = (edges: FinancialReportConnection['edges'], langField: typeof LANG_FIELD) => {
  return [...edges].sort((a, b) => {
    const aMeta = getReportSortMeta(a.node.name[langField] || a.node.name.en || '')
    const bMeta = getReportSortMeta(b.node.name[langField] || b.node.name.en || '')

    if (aMeta.year !== bMeta.year) return bMeta.year - aMeta.year
    return aMeta.typeRank - bMeta.typeRank
  })
}
const localRecords = financialReportsData as FinancialReportConnection

const FinancialReportsEnPage: React.FC<FinancialReportsEnPageProps> = ({ data, location }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const records = data?.records?.totalCount ? data.records : localRecords
  const { totalCount } = records
  const edges = sortFinancialReportEdges(records.edges, LANG_FIELD)

  const endIdx = PER_PAGE * currentPage
  const startIdx = endIdx - PER_PAGE
  const isFirst = currentPage === 1
  const isLast = currentPage === Math.ceil(totalCount / PER_PAGE)
  const currentItems = edges.slice(startIdx, endIdx)

  return (
    <Layout langKey={LANG_KEY} fluid={true} currentPath={location.pathname}>
      <SEO title={PAGE_TITLE} lang="en" />
      <HeroBanner />
      <PageTitle title={PAGE_TITLE} narrow />

      <ReportsGrid>
        {currentItems.length > 0 ? (
          currentItems.map(({ node }, idx) => {
            if (!node.name[LANG_FIELD]) return null
            const fileUrl = get(node, `file[${LANG_FIELD}].asset.url`)
            const thumbnailAsset = node.thumbnail?.asset

            return (
              <ReportCard key={idx} href={fileUrl} target="_blank" rel="noreferrer">
                {thumbnailAsset?.url ? (
                  <ReportThumbnailImg src={thumbnailAsset.url} alt={node.name[LANG_FIELD]} height={300} />
                ) : null}
                <ReportName>{node.name[LANG_FIELD]}</ReportName>
              </ReportCard>
            )
          })
        ) : (
          <EmptyState langKey={LANG_KEY} />
        )}
      </ReportsGrid>

      <Pagination
        langKey={LANG_KEY}
        perPage={PER_PAGE}
        isFirst={isFirst}
        isLast={isLast}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        noRecord={totalCount === 0}
        totalCount={totalCount}
      />
    </Layout>
  )
}

export default FinancialReportsEnPage
